import React, { useCallback, useEffect, useState, useRef } from "react";
import "./SidebarNavigation.css";
import CompanyLogo from "../../../assets/images/Futuresoft_logo.jpg";
import { Modal } from "react-bootstrap";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  saveAuthData,
  saveStoreData,
} from "../../../features/login/LoginSlice";
import { apiHandler } from "../../../api";

// Icons
import {
  Users,
  AlertCircle,
  User,
  FileText,
  Menu,
  BarChart3,
  ShoppingCart,
} from "lucide-react";
import { FiLogOut } from "react-icons/fi";
import usePermission from "../../../utils/usePermission";

// Constants
const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 300;
const PAGE_SIZE = 100;

const SidebarNavigation = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authData, storeId, userDetails } = useSelector(
    (state) => state.loginSlice
  );

  // --- State ---
  const [openSelectStore, setOpenSelectStore] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(storeId);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Change state to an empty object to store grouped data
  const [stores, setStores] = useState({});
  const [service, setService] = useState("IGM");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // --- Refs ---
  const debounceTimer = useRef(null); // For debouncing

  // --- Selectors ---
  const rawBrandName =
    useSelector((state) => state.loginSlice.brandName) ||
    localStorage.getItem("brandName");
  const displayBrandName =
    rawBrandName && rawBrandName !== "undefined" ? rawBrandName : "";

  const queryParams = new URLSearchParams(window.location.search);
  const tenantIdFromQuery =
    queryParams.get("tenant_id") ||
    queryParams.get("tenantId") ||
    queryParams.get("storeId");
  const effectiveStoreId = tenantIdFromQuery || storeId;

  const userData = useSelector((state) => state.loginSlice);
  const isAdmin = userData?.isAdmin;
  let canRead = usePermission("IGM_MULTI_STORES");

  // --- Derived State (Memoization helps performance but plain function is fine here) ---
  const allStores = [];
  if (stores && typeof stores === "object") {
    // Flattens the grouped stores object into a single array for iteration
    Object.values(stores).forEach((storeList) => {
      if (Array.isArray(storeList)) {
        allStores.push(...storeList);
      }
    });
  }

  useEffect(() => {
    if (effectiveStoreId && selectedStoreId !== effectiveStoreId) {
      setSelectedStoreId(effectiveStoreId);
    }
  }, [effectiveStoreId, selectedStoreId]);

  useEffect(() => {
    const hasBrandName = displayBrandName && displayBrandName.trim() !== "";
    if (!effectiveStoreId || hasBrandName) {
      return;
    }

    let matchedStore = null;
    if (stores && typeof stores === "object") {
      Object.values(stores).some((storeList) => {
        if (!Array.isArray(storeList)) {
          return false;
        }

        const found = storeList.find(
          (store) => String(store.id) === String(effectiveStoreId)
        );
        if (found) {
          matchedStore = found;
          return true;
        }

        return false;
      });
    }

    if (matchedStore?.brand_name) {
      dispatch(
        saveStoreData({
          storeId: matchedStore.id,
          brandName: matchedStore.brand_name,
        })
      );
    }
  }, [stores, effectiveStoreId, displayBrandName, dispatch]);

  // --- Handlers ---
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // --- Store Fetching & Search ---
  const fetchStores = useCallback(
    async (searchQuery = "") => {
      // if (!canRead) return;

      try {
        setLoading(true);
        setError(null);

        const response = await apiHandler({
          url: `https://identitybackend.ondchub.com/api/v1/tenant/getServiceTenants?iPageSize=${PAGE_SIZE}&sSearchByName=${encodeURIComponent(
            searchQuery
          )}&sService=${encodeURIComponent(service)}`,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        if (response?.data?.statusCode === 200) {
          // Store the grouped object directly
          setStores(response?.data?.data || {});
          setError(null);
        } else {
          setStores({}); // Clear stores on failure
          setError(response?.data?.message);
        }
      } catch (err) {
        //console.error("Error fetching stores:", err);
        setStores({}); // Clear stores on error
        setError("Failed to fetch stores");
      } finally {
        setLoading(false);
      }
    },
    [canRead, service, authData, dispatch, storeId] // Dependencies for fetchStores
  );

  const debouncedSearch = useCallback(
    (searchQuery) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (searchQuery === "" || searchQuery.length >= MIN_SEARCH_LENGTH) {
        debounceTimer.current = setTimeout(() => {
          fetchStores(searchQuery);
        }, DEBOUNCE_DELAY);
      }
    },
    [fetchStores] // Dependency for debouncedSearch
  );

  const handleStoreSearch = (e) => {
    const searchQuery = e.target.value;
    setSearch(searchQuery);
    debouncedSearch(searchQuery);
  };

  // Initial fetch
  useEffect(() => {
    fetchStores(search);
  }, [fetchStores, search]); // Runs when component mounts or search/fetchStores changes

  const handleChangeStore = (store) => {
    setSelectedStoreId(store.id);
    dispatch(
      saveStoreData({
        storeId: store.id,
        brandName: store.brand_name,
      })
    );
   setOpenSelectStore(false);
    navigate("/");
  };

  // --- Navigation ---
  const navigationItems = [
    {
      id: "analytics",
      label: "Analytics",
      icon: BarChart3, // Best suited for analytics
      path: "/",
    },
    {
      id: "orders",
      label: "Orders",
      icon: ShoppingCart, // Best suited for orders
      path: "/orders",
    },

    {
      id: "issues-buyer",
      label: "Counter Party Issues",
      icon: Users,
      path: "/issues/buyer",
    },
    {
      id: "issues-seller",
      label: "Reported Issues",
      icon: AlertCircle,
      path: "/issues/seller",
    },
    {
      id: "contact-us",
      label: "Contact Us",
      icon: User,
      path: "/contact-us",
    },
    { id: "faq", label: "FAQ", icon: FileText, path: "/faq" },
  ];

  const isTabActive = (itemPath) => {
    // If the item path is the root, check for an exact match
    if (itemPath === "/") {
      return location.pathname === "/";
    }
    // For all other paths, use startsWith to handle sub-routes
    return location?.pathname?.startsWith(itemPath) ?? false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    navigate("/");
    dispatch(saveAuthData(false));
  };

  // --- Render ---
  return (
    <div className="sidebar-container">
      <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          <div className="logo-section">
            {!sidebarCollapsed && (
              <div className="logo-text">
                <div className="brand-name">
                  <img className="OndcLogo" src={CompanyLogo} alt="Logo" />
                </div>
                <div className="brand-subtitle">IGM Framework</div>
              </div>
            )}
          </div>
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Menu />
          </button>
        </div>
        <nav className="nav-section">
          {navigationItems.map((item) => (
            <div key={item.id} className="nav-item">
              <Link
                to={item.path}
                className={`nav-link ${isTabActive(item.path) ? "active" : ""}`}
                title={sidebarCollapsed ? item.label : ""}
              >
                <div className="nav-content">
                  <item.icon className="nav-icon" />
                  {!sidebarCollapsed && (
                    <span className="nav-text">{item.label}</span>
                  )}
                </div>
                {isTabActive(item.path) && <div className="active-indicator" />}
              </Link>
            </div>
          ))}
        </nav>

        {isAdmin && (
        <div className="d-flex justify-content-left p-3">
          <div className="store-container">
            <div className="active-store">
              <span className="label">Active Store:</span>
              <span className="name">
                {displayBrandName ||
                  (effectiveStoreId
                    ? `Tenant ${effectiveStoreId}`
                    : "No store selected")}
              </span>
            </div>
            <button
              onClick={() => setOpenSelectStore(true)}
              className="btn btn-primary btn-sm px-4 py-2 text-white font-weight-bold rounded-3 shadow-sm hover:shadow-lg transition-all"
              style={{ fontSize: "0.875rem" }}
            >
              Change Store
            </button>
          </div>
        </div>
       )}

        {/* User/Logout Section */}
        <div className="user-section">
          <button
            className="logout-button"
            onClick={handleSubmit}
            title="Logout"
          >
            <FiLogOut size={20} /> {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {/* Main Content (Uncommented as it's typically needed for routing) */}
      {/* <div className={`main-content ${sidebarCollapsed ? "collapsed" : ""}`}>
        <Outlet />
      </div> */}

      {/* Change Store Modal */}
      <Modal
        centered
        show={openSelectStore}
        onHide={() => setOpenSelectStore(false)}
      >
        <Modal.Header closeButton>Change Store</Modal.Header>
        <Modal.Body>
          <div className="store-selector-card">
            <div className="search-section">
              <div className="search-input-group">
                <input
                  type="search"
                  name="search"
                  id="storeSearch"
                  value={search}
                  onChange={handleStoreSearch}
                  className="form-control search-input"
                  placeholder="Search tenants by name..."
                />
              </div>
            </div>
            {/* Display list based on the flattened allStores array */}
            <div
              className="accordion-body p-2"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              {loading ? (
                <div className="text-muted text-center py-3">Loading...</div>
              ) : error ? (
                <div className="text-danger text-center py-3">{error}</div>
              ) : allStores.length > 0 ? (
                allStores.map((store) => (
                  <div
                    key={store.id}
                    className={`card mb-2 border-0 shadow-sm ${
                      selectedStoreId === store.id
                        ? "border-primary bg-info bg-opacity-25"
                        : ""
                    }`}
                    onClick={() => handleChangeStore(store)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-body d-flex align-items-center p-2">
                      <img
                        src={store.brand_logo}
                        alt={store.brand_name}
                        className="me-3 rounded border"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/60";
                        }}
                      />
                      <div>
                        <h6 className="mb-0 text-truncate">
                          {store.brand_name} ({store.type})
                        </h6>
                        <small className="text-muted">ID: {store.id}</small>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted text-center py-3">
                  No stores available.
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SidebarNavigation;
